/**
 * The languages this workspace builds: the source locale plus the three
 * translations declared in each app's `project.json` (`i18n.locales`). A
 * language that is not in here has no bundle to redirect to.
 */
export const SUPPORTED_LANGUAGES = ['de', 'fr', 'it', 'en'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export function isSupportedLanguage(language: string): language is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(language);
}

/**
 * A language tag we are willing to read: the two-letter code on its own, or
 * followed by a region we ignore (`de`, `DE`, `de-CH`, `de_CH`).
 *
 * Matching the shape rather than taking the first two characters matters because
 * the inputs are not all trustworthy — the cookie is whatever the browser holds,
 * and `sprache` is whatever the `Lokalisierung` column holds. Truncating would
 * read `english` as `en` and `destroy` as `de`, quietly turning a value that
 * names no language into a redirect.
 */
const LANGUAGE_TAG_PATTERN = /^([a-z]{2})(?:[-_].*)?$/i;

/**
 * Reduces a language tag to one of the built languages, or `null` when it is
 * none of them.
 *
 * Region and case have to go first: `LOCALE_ID` carries `de-CH` in some builds,
 * `navigator.language` almost always does, and the profile's `sprache` mirrors
 * the backend's `Sprache.Lokalisierung` column, whose casing the backend does not
 * guarantee either. Comparing the raw value against the supported list therefore
 * rejects perfectly valid languages, and every caller of this used to turn that
 * rejection into a redirect to its fallback language.
 */
export function normalizeLanguage(language: string | null | undefined): SupportedLanguage | null {
  const candidate = LANGUAGE_TAG_PATTERN.exec(language?.trim() ?? '')?.[1].toLowerCase();
  return candidate && isSupportedLanguage(candidate) ? candidate : null;
}
